import {DisplayObject, Sprite} from 'pixi.js';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {AUTOMATION_META, AutomationMeta} from '@gongxigames/framework/core/fragments/Autotest';

export enum State {
    UP = 'UP',
    DOWN = 'DOWN',
    OVER = 'OVER',
    DISABLED = 'DISABLED'
}

type StateMap = {
    [key in State]?: DisplayObject;
};

export class Button extends Sprite {
    protected _state!: State;
    protected _frames: StateMap = {};
    protected _callback?: Function;
    protected _over = false;
    private _enabled = false;

    constructor(callback?: Function) {
        super();

        this._callback = callback;

        this.interactive = true;

        this.enable();

        this.on('mousedown', this.onMouseDown);
        this.on('mouseup', this.onMouseUp);
        this.on('mouseover', this.onMouseOver);
        this.on('mouseout', this.onMouseOut);
        this.on('click', this.onClick);

        this.on('tap', this.onClick);
        this.on('touchstart', this.onMouseDown);
        this.on('touchendoutside', this.onMouseUp);
        this.on('touchend', this.onMouseUp);
        this.on('touchendoutside', this.onMouseUp);
    }

    public setFrames(up: DisplayObject, down?: DisplayObject, over?: DisplayObject, disabled?: DisplayObject): void {
        this._frames[State.UP] = up;
        this._frames[State.DOWN] = down;
        this._frames[State.OVER] = over;
        this._frames[State.DISABLED] = disabled;

        this.updateFrame();
    }

    public enable(): void {
        this._enabled = true;
        this.buttonMode = true;
        this.interactive = true;

        if (this._over) {
            this.state = State.OVER;
        } else {
            this.state = State.UP;
        }
    }

    public disable(): void {
        this.state = State.DISABLED;
        this._enabled = false;
        this.buttonMode = false;
        this.interactive = false;
        this._over = false;
    }

    public setDisabled(disabled: boolean): void {
        disabled ? this.disable() : this.enable();
    }

    public set state(value: State) {
        if (!this._enabled) {
            return;
        }

        this.updateState(value);
    }

    public get state(): State {
        return this._state;
    }

    public set callback(value: Function) {
        this._callback = value;
    }

    public get frames(): StateMap {
        return this._frames;
    }

    public get enabled(): boolean {
        return this._enabled;
    }

    protected onClick(): void {
        if (!this._enabled) {
            return;
        }

        this.state = State.OVER;

        if (this._callback) {
            this._callback();
        } else {
            consoleLogger.warn('There is no a callback.');
        }
    }

    protected updateState(value: State): void {
        if (this._state !== value) {
            this._state = value;

            this.removeChildren();
            this.updateFrame();
        }
    }

    protected updateFrame(): void {
        if (this._frames[this._state]) {
            this.addChild(this._frames[this._state]!);
        }
    }

    protected updateHitArea(): void {
        this.hitArea = this.getLocalBounds();
    }

    protected onMouseUp(): void {
        this.state = State.UP;
    }

    protected onMouseDown(): void {
        this.state = State.DOWN;
    }

    protected onMouseOver(): void {
        this._over = true;
        this.state = State.OVER;
    }

    protected onMouseOut(): void {
        this._over = false;
        this.state = State.UP;
    }

    [AUTOMATION_META](): AutomationMeta {
        return {
            type: 'button',
            disabled: !this._enabled,
            over: this._over,
            pressed: this.state === State.DOWN
        };
    }
}
